import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarRange, RefreshCw, Plus, AlertCircle, Pencil, Copy, Power, Trash2, X, Loader2,
} from 'lucide-react';
import {
  listEvents, createEvent, toggleEvent, duplicateEvent, deleteEvent, reloadEvents,
  type EventSummary, type EventCreateInput,
} from '@/shared/services/eventManagerService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';
import {
  EVENT_TYPES, TYPE_FILTER_OPTIONS, eventTypeDef,
  EventTypeBadge, EventStatusBadge, fmtDate, fmtDateTime,
} from './eventTypes';

type StatusFilter = 'all' | 'active' | 'inactive' | 'scheduled' | 'expired';
const STATUS_TABS: { v: StatusFilter; label: string }[] = [
  { v: 'all', label: 'Todos' }, { v: 'active', label: 'Ativos' },
  { v: 'inactive', label: 'Inativos' }, { v: 'scheduled', label: 'Agendados' },
  { v: 'expired', label: 'Expirados' },
];

export default function EventsPage() {
  const { notify } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [type, setType] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [toDelete, setToDelete] = useState<EventSummary | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setItems(await listEvents({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(type !== 'all' ? { type } : {}),
        ...(status !== 'all' ? { status } : {}),
      }));
    } catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, [search, type, status]);

  useEffect(() => { const t = setTimeout(fetchData, 200); return () => clearTimeout(t); }, [fetchData]);

  const handleReload = async () => {
    setReloading(true);
    try {
      const r: any = await reloadEvents();
      notify('success', `Eventos atualizados${r?.rows ? ` (${r.rows})` : ''} — servidor vai recarregar`);
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setReloading(false); }
  };

  const handleToggle = async (e: EventSummary) => {
    const on = !e.useFlag;
    if (!confirm(`${on ? 'Ativar' : 'Desativar'} "${e.name}" (#${e.eventId})?`)) return;
    try { await toggleEvent(e.eventId, on); notify('success', on ? 'Evento ativado' : 'Evento desativado'); await fetchData(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const handleDuplicate = async (e: EventSummary) => {
    try {
      const dup = await duplicateEvent(e.eventId);
      notify('success', `Duplicado como #${dup.eventId}`);
      navigate(`/admin/events/${dup.eventId}`);
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const rowActions = (e: EventSummary) => (
    <RowActions actions={[
      { label: 'Editar', icon: <Pencil size={13} />, onClick: () => navigate(`/admin/events/${e.eventId}`), variant: 'warning' },
      { label: 'Duplicar', icon: <Copy size={13} />, onClick: () => handleDuplicate(e) },
      { label: e.useFlag ? 'Desativar' : 'Ativar', icon: <Power size={13} />, onClick: () => handleToggle(e) },
      { label: 'Excluir', icon: <Trash2 size={13} />, onClick: () => setToDelete(e), variant: 'danger' },
    ]} />
  );

  const columns: DataTableColumn<EventSummary>[] = [
    { key: 'id', label: 'ID', width: '64px', cell: (e) => <span className="font-mono text-xs text-white/50">{e.eventId}</span> },
    { key: 'name', label: 'Evento', width: '1fr', cell: (e) => (
      <div className="min-w-0">
        <p className="text-sm font-bold text-white truncate">{e.name || '(sem nome)'}</p>
        <div className="mt-1"><EventTypeBadge type={e.eventType} /></div>
      </div>
    ) },
    { key: 'period', label: 'Período', width: '150px', mobileHidden: true, cell: (e) => (
      <span className="text-[11px] text-white/50">{fmtDate(e.beginDate)} → {fmtDate(e.endDate)}</span>
    ) },
    { key: 'status', label: 'Status', width: '110px', cell: (e) => <EventStatusBadge status={e.status} /> },
    { key: 'actions', label: '', width: '48px', align: 'right', mobileHidden: true, cell: rowActions },
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <CalendarRange className="text-[#00e1d7]" size={26} />
            Eventos
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {items.length.toLocaleString('pt-BR')} eventos · EventData · gerenciamento in-game
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleReload} disabled={reloading}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium
                       bg-white/[0.04] text-white/80 hover:bg-white/[0.08] transition cursor-pointer disabled:opacity-50">
            <RefreshCw size={14} className={reloading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button onClick={() => setCreating(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold btn-primary transition cursor-pointer">
            <Plus size={14} /> Novo Evento
          </button>
        </div>
      </div>

      {/* filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex rounded-lg bg-white/[0.03] border border-white/[0.06] p-0.5">
          {STATUS_TABS.map(t => (
            <button key={t.v} onClick={() => setStatus(t.v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition cursor-pointer
                ${status === t.v ? 'bg-[#00e1d7]/15 text-[#00e1d7]' : 'text-white/55 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={type} onChange={(e) => setType(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white cursor-pointer">
          <option value="all">Todos os tipos</option>
          {TYPE_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar evento ou ID..."
          className="flex-1 min-w-[160px] bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<EventSummary>
        columns={columns} rows={items} rowKey={(e) => e.eventId} loading={loading}
        empty="Nenhum evento" onRowClick={(e) => navigate(`/admin/events/${e.eventId}`)}
        mobileTitle={(e) => e.name || '(sem nome)'}
        mobileSubtitle={(e) => `#${e.eventId} · ${eventTypeDef(e.eventType).label} · ${fmtDateTime(e.lastModifiedDate)}`}
        mobileActions={rowActions}
      />

      {creating && <NewEventModal onClose={() => setCreating(false)}
        onCreated={(id) => { setCreating(false); navigate(`/admin/events/${id}`); }} />}
      {toDelete && <DeleteEventModal event={toDelete} onClose={() => setToDelete(null)}
        onDeleted={async () => { setToDelete(null); await fetchData(); }} />}
    </div>
  );
}

/* ------------------------------------------------ Novo Evento */
function NewEventModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const { notify } = useToast();
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 864e5);
  const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [form, setForm] = useState<EventCreateInput>({
    eventType: 2, name: '', beginDate: iso(now), endDate: iso(in30),
    serverIndex: '1', worldIndex: 1, npcIndex: 49,
  });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof EventCreateInput, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { notify('error', 'Nome obrigatório'); return; }
    setBusy(true);
    try { const ev = await createEvent(form); notify('success', `Evento #${ev.eventId} criado`); onCreated(ev.eventId); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Novo Evento</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Tipo</label>
            <select value={form.eventType} onChange={(e) => set('eventType', Number(e.target.value))}
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white cursor-pointer">
              <optgroup label="Com editor">
                {[2, 11, 14].map(t => <option key={t} value={t}>{EVENT_TYPES[t].label} — {EVENT_TYPES[t].description}</option>)}
              </optgroup>
              <optgroup label="Sem editor (metadados)">
                {Object.entries(EVENT_TYPES).filter(([t]) => ![2, 11, 14].includes(Number(t)))
                  .map(([t, d]) => <option key={t} value={t}>{d.label}</option>)}
              </optgroup>
            </select>
          </div>
          <Field label="Nome" value={form.name} onChange={(v) => set('name', v)} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Início</label>
              <input type="datetime-local" value={form.beginDate} onChange={(e) => set('beginDate', e.target.value)}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Fim</label>
              <input type="datetime-local" value={form.endDate} onChange={(e) => set('endDate', e.target.value)}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Server Index" value={form.serverIndex ?? ''} onChange={(v) => set('serverIndex', v)} mono />
            <NumField label="World" value={form.worldIndex ?? 1} onChange={(v) => set('worldIndex', v)} />
            <NumField label="NPC" value={form.npcIndex ?? 49} onChange={(v) => set('npcIndex', v)} />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">Cancelar</button>
          <button onClick={submit} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Criar e configurar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------ Excluir (confirmação forte) */
function DeleteEventModal({ event, onClose, onDeleted }: { event: EventSummary; onClose: () => void; onDeleted: () => void }) {
  const { notify } = useToast();
  const [txt, setTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const ok = txt.trim() === String(event.eventId);

  const doDelete = async () => {
    if (!ok) return;
    setBusy(true);
    try { await deleteEvent(event.eventId); notify('success', 'Evento excluído'); onDeleted(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full border-red-500/20" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <Trash2 size={18} className="text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-white">Excluir evento</h2>
        </div>
        <p className="text-sm text-white/65 mb-1">Isso apaga <b className="text-white">{event.name}</b> e toda a config dele (slots, pools, rounds). Recursos globais compartilhados só são removidos se ficarem órfãos.</p>
        <p className="text-xs text-white/45 mb-3">Digite <span className="font-mono text-red-300">{event.eventId}</span> para confirmar.</p>
        <input value={txt} onChange={(e) => setTxt(e.target.value)} autoFocus placeholder={String(event.eventId)}
          className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white font-mono mb-4" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">Cancelar</button>
          <button onClick={doDelete} disabled={!ok || busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-sm transition cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

/* helpers de campo */
function Field({ label, value, onChange, mono }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`} />
    </div>
  );
}
function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white font-mono" />
    </div>
  );
}
